import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

import { encryptCredentials, fetchServerTimeAndNonce } from "../utils/auth";

const authAPIEndpoint = `/api/auth`;

const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Unable to decode JWT payload", error);
    return null;
  }
};

const redirectAfterLogin = (redirectUri, accessToken, idToken, refreshToken) => {
  // accessToken / idToken / refreshToken are intentionally NOT written to
  // cookies or localStorage here (VAPT — M10-10258). `auth-backend`
  // `/auth/sso/token` redeems the code and sets the httpOnly `refreshToken`
  // cookie on `.medvol.in` — the only cross-app handoff; every consuming app
  // calls `/auth/refresh` on bootstrap to recover access/id tokens. The
  // localhost dev branch below still uses query string for the cross-port
  // dev flow (the backend returns tokens in the body only for that case).

  if (redirectUri) {
    if (
      redirectUri.includes("localhost") &&
      ["DEV", "STAGE"].includes(process.env.REACT_APP_ENVIRONMENT)
    ) {
      window.location.href =
        redirectUri +
        "?token=" +
        accessToken +
        "&idToken=" +
        idToken +
        "&refreshToken=" +
        refreshToken;
    } else {
      window.location.href = redirectUri;
    }
  } else {
    window.location.href = "/";
  }
};

function SsoCallback() {
  const [statusMessage, setStatusMessage] = useState(
    "Finishing your SSO login..."
  );
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const codeParam = searchParams.get("code");

  const cachedRedirectUri = useMemo(
    () => localStorage.getItem("medvol_redirect_uri") || "",
    []
  );

  useEffect(() => {
    if (!codeParam) {
      setStatusMessage("Unable to complete SSO login.");
      setErrorMessage("Missing authorization code in callback.");
      return;
    }

    const exchangeCodeForSession = async () => {
      setStatusMessage("Validating authorization code...");
      try {
        const isReactNativeApp =
          window.isReactNativeApp ||
          window.navigator.userAgent.includes("MyReactNativeAppWebView");

        // The code is redeemed by auth-backend (not the browser) so the
        // refresh token is set as an httpOnly cookie and never touches JS.
        // `sso_redirect_uri` must match the redirect_uri sent to
        // /oauth2/authorize in useSso.
        const { serverTime, nonce } = await fetchServerTimeAndNonce(authAPIEndpoint);
        const credentials = await encryptCredentials({
          code: codeParam,
          sso_redirect_uri: `${window.location.origin}/sso/callback`,
          redirect_uri: cachedRedirectUri,
          client: isReactNativeApp ? "webview" : "web",
          timestamp: serverTime,
          nonce,
        });

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Basic ${btoa(
            `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`,
          )}`,
        };

        const { data } = await axios.post(
          `${authAPIEndpoint}/sso/token`,
          { credentials },
          { headers, withCredentials: true },
        );

        if (data?.status !== 200) {
          throw new Error(data?.message || "Unable to complete SSO login");
        }

        // Tokens are only in the body for the WebView / localhost-dev cases.
        const userPayload = data.idToken ? decodeJwtPayload(data.idToken) : null;

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: {
            user: userPayload,
          },
        });

        if (isReactNativeApp && window.ReactNativeWebView?.postMessage) {
          // Send tokens to React Native app similar to mobile login flow
          const response = {
            AuthenticationResult: {
              AccessToken: data.accessToken,
              IdToken: data.idToken,
              RefreshToken: data.refreshToken || "",
            },
            user: userPayload,
          };

          setTimeout(() => {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "storeResponse", response })
            );
          }, 2000);
        } else {
          redirectAfterLogin(
            cachedRedirectUri,
            data.accessToken,
            data.idToken,
            data.refreshToken || ""
          );
        }
      } catch (error) {
        console.error("SSO login failed:", error);
        setStatusMessage("Unable to complete SSO login.");
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to finish SSO login. Please try again or login manually."
        );
      }
    };

    exchangeCodeForSession();
  }, [cachedRedirectUri, codeParam, dispatch]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f9fafb",
        padding: "24px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "480px",
          width: "100%",
          boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
        }}
      >
        <h2 style={{ marginBottom: "12px" }}>Medvol SSO</h2>
        <p>{statusMessage}</p>
        {errorMessage && (
          <p style={{ color: "#dc2626", marginTop: "16px" }}>{errorMessage}</p>
        )}
      </div>
    </div>
  );
}

export default SsoCallback;

